import { AlertCircle, ChevronRight, MapPin, Video } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PercentBar from "@/components/common/PercentBar";
import { MatchedClient } from "@/types/staffing/matchedClient";
import { formatLookupValues, getClientStatusBadgeColor } from "@/utils/staffingUtil";
import { cn } from "@/lib/utils";
import { OpeningsBlocker } from "../hooks/useOpenings";

/**
 * The Openings widget never shows client names to staff (see the isWidget
 * branch of ClientStaffingContextMenu) - only initials. Keep that here.
 */
export const toInitials = (name: string | null) =>
    name?.split(' ').map(p => p[0]).join('').toUpperCase() ?? '';

/** A labelled value, stacked so it stays readable on a narrow screen. */
export const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium break-words">{value === null || value === undefined || value === '' ? '-' : value}</div>
    </div>
);

/** A named match percentage with its bar. */
export const MatchRow = ({ label, percent }: { label: string; percent: number }) => (
    <div className="flex items-center gap-3">
        <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
        <div className="flex-1">
            <PercentBar percent={percent} />
        </div>
    </div>
);

export const OpeningInitials = ({ clientName, className }: { clientName: string | null; className?: string }) => (
    <div
        className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary",
            className
        )}
        aria-hidden
    >
        {toInitials(clientName) || '?'}
    </div>
);

/**
 * Explains why there are no openings to show. A failed identity lookup is not the
 * user's profile being incomplete, so the two cases must not share a message.
 */
export const OpeningsBlockerAlert = ({ blocker }: { blocker: OpeningsBlocker }) => {
    if (!blocker) return null;

    return blocker === 'session' ? (
        <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Couldn't verify your session</AlertTitle>
            <AlertDescription>
                We couldn't load your profile, so there is nothing to match openings against.
                Close and reopen this screen, and let IT know if it keeps happening.
            </AlertDescription>
        </Alert>
    ) : (
        <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No region on your profile</AlertTitle>
            <AlertDescription>
                Openings are matched by region. Contact your coordinator if this looks wrong.
            </AlertDescription>
        </Alert>
    );
};

export const OpeningBadges = ({ opening }: { opening: MatchedClient }) => (
    <div className="flex flex-wrap items-center gap-1.5">
        {opening.clientStatus && (
            <Badge variant="outline" className={getClientStatusBadgeColor(opening.clientStatus)}>
                {opening.clientStatus}
            </Badge>
        )}
        {opening.isTelehealth && (
            <Badge variant="secondary" className="gap-1">
                <Video className="h-3 w-3" />
                Telehealth
            </Badge>
        )}
        {opening.postingID == null && (
            <Badge variant="outline" className="text-muted-foreground">No active posting</Badge>
        )}
    </div>
);

interface OpeningCardProps {
    opening: MatchedClient;
    onSelect: (opening: MatchedClient) => void;
}

/** One opening, rendered as a tappable card instead of a table row. */
const OpeningCard = ({ opening, onSelect }: OpeningCardProps) => (
    <Card
        role="button"
        tabIndex={0}
        onClick={() => onSelect(opening)}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(opening);
            }
        }}
        className="cursor-pointer transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
        <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
                <OpeningInitials clientName={opening.clientName} />
                <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">
                        {opening.postingID != null ? `Opening #${opening.postingID}` : 'Potential match'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                            {opening.parentCaregiver1City ?? 'City unknown'} &middot; {opening.distance.toFixed(1)} mi
                        </span>
                    </div>
                </div>
                <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
            </div>

            <OpeningBadges opening={opening} />

            <PercentBar percent={opening.percentMatch} />

            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Age" value={opening.age} />
                <Field label="Gender" value={opening.gender} />
                <Field label="Funding Source" value={formatLookupValues(opening.insuranceCompany)} />
                <Field label="Language" value={formatLookupValues(opening.clientLang)} />
            </div>
        </CardContent>
    </Card>
);

export default OpeningCard;
